import React from "react";
import { FACILITY_LABELS } from "@/lib/api";

const PRESET_RATINGS = [4.5, 4.0, 3.5];

export default function FilterSidebar({ filters, setFilters, regions, operators, facilities }) {
  const update = (patch) => setFilters({ ...filters, ...patch });
  const toggleArr = (key, value) => {
    const cur = filters[key] || [];
    const next = cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value];
    update({ [key]: next });
  };

  return (
    <aside className="lg:sticky lg:top-20 bg-white border border-border rounded-2xl p-5 space-y-6" data-testid="filter-sidebar">
      <div className="flex items-center justify-between">
        <h3 className="font-display font-semibold text-lg">Filters</h3>
        <button
          className="text-xs text-forest hover:underline"
          onClick={() => setFilters({})}
          data-testid="filter-clear-all"
        >
          Clear all
        </button>
      </div>

      <div>
        <div className="label-eyebrow mb-2">Price / night</div>
        <div className="flex items-center gap-2 text-sm">
          <input
            type="number"
            placeholder="Min £"
            value={filters.price_min || ""}
            onChange={(e) => update({ price_min: e.target.value ? Number(e.target.value) : undefined })}
            className="w-full bg-sand border border-border rounded-lg px-3 py-2 outline-none focus:border-forest"
            data-testid="filter-price-min"
          />
          <span className="text-ink/40">–</span>
          <input
            type="number"
            placeholder="Max £"
            value={filters.price_max || ""}
            onChange={(e) => update({ price_max: e.target.value ? Number(e.target.value) : undefined })}
            className="w-full bg-sand border border-border rounded-lg px-3 py-2 outline-none focus:border-forest"
            data-testid="filter-price-max"
          />
        </div>
      </div>

      <div>
        <div className="label-eyebrow mb-2">Rating</div>
        <div className="flex flex-wrap gap-1.5">
          {PRESET_RATINGS.map((r) => (
            <button
              key={r}
              onClick={() => update({ rating_min: filters.rating_min === r ? undefined : r })}
              className={`text-xs rounded-full px-3 py-1.5 border ${filters.rating_min === r ? "bg-forest text-white border-forest" : "bg-white border-border hover:border-forest"}`}
              data-testid={`filter-rating-${r}`}
            >
              {r}+ ★
            </button>
          ))}
        </div>
      </div>

      <div>
        <div className="label-eyebrow mb-2">Region</div>
        <select
          value={filters.region || ""}
          onChange={(e) => update({ region: e.target.value || undefined })}
          className="w-full bg-sand border border-border rounded-lg px-3 py-2 text-sm outline-none focus:border-forest"
          data-testid="filter-region"
        >
          <option value="">All UK regions</option>
          {regions?.map((r) => (
            <option key={r.name} value={r.name}>{r.name} ({r.count})</option>
          ))}
        </select>
      </div>

      <div>
        <div className="label-eyebrow mb-2">Operator</div>
        <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
          {operators?.map((op) => (
            <label key={op.name} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`filter-operator-${op.name.replace(/\s+/g,'-').toLowerCase()}`}>
              <input
                type="checkbox"
                checked={(filters.operator || []).includes(op.name)}
                onChange={() => toggleArr("operator", op.name)}
                className="accent-forest"
              />
              <span>{op.name}</span>
              <span className="ml-auto text-xs text-ink/40">{op.count}</span>
            </label>
          ))}
        </div>
      </div>

      <div>
        <div className="label-eyebrow mb-2">Facilities</div>
        <div className="grid grid-cols-1 gap-1.5 max-h-64 overflow-y-auto pr-1">
          {facilities?.map((f) => (
            <label key={f.name} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`filter-facility-${f.name}`}>
              <input
                type="checkbox"
                checked={(filters.facility || []).includes(f.name)}
                onChange={() => toggleArr("facility", f.name)}
                className="accent-forest"
              />
              <span>{FACILITY_LABELS[f.name] || f.name}</span>
              <span className="ml-auto text-xs text-ink/40">{f.count}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="filter-pet-friendly">
          <input type="checkbox" checked={!!filters.pet_friendly} onChange={(e) => update({ pet_friendly: e.target.checked || undefined })} className="accent-forest" />
          Pet friendly
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="filter-adults-only">
          <input type="checkbox" checked={!!filters.adults_only} onChange={(e) => update({ adults_only: e.target.checked || undefined })} className="accent-forest" />
          Adults only
        </label>
      </div>
    </aside>
  );
}
